import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useState, useCallback, useRef } from "react";
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut as firebaseSignOut, sendPasswordResetEmail, updateProfile, } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase/config.js";
import { AuthContext } from "./AuthContext.js";
const isDev = import.meta.env?.VITE_USE_EMULATOR === 'true';
async function resolveUserRole(fbUser) {
    // Retry up to 3 times with a small delay — Firestore rules may take a moment to propagate
    for (let attempt = 0; attempt < 3; attempt++) {
        try {
            const userDoc = await getDoc(doc(db, "users", fbUser.uid));
            if (userDoc.exists()) {
                const role = userDoc.data().role;
                if (role)
                    return role;
            }
        }
        catch (err) {
            console.warn(`[Auth] Failed to resolve user role (attempt ${attempt + 1}/3):`, err);
        }
        if (attempt < 2) {
            await new Promise((r) => setTimeout(r, 300));
        }
    }
    console.warn("[Auth] Could not resolve user role after 3 attempts, defaulting to null");
    return null;
}
async function mapFirebaseUser(fbUser) {
    const role = await resolveUserRole(fbUser);
    let data = {};
    try {
        const userDoc = await getDoc(doc(db, "users", fbUser.uid));
        if (userDoc.exists())
            data = userDoc.data();
    }
    catch (err) {
        console.warn("[Auth] Failed to fetch user doc from Firestore:", err);
    }
    return {
        uid: fbUser.uid,
        email: fbUser.email,
        displayName: fbUser.displayName,
        photoURL: fbUser.photoURL,
        role: role || "customer",
        businessId: data.businessId,
        emailVerified: fbUser.emailVerified,
        // Map additional Firestore fields
        vehicleType: data.vehicleType,
        licenseType: data.licenseType,
        yearsExperience: data.yearsExperience,
        rating: data.rating,
        totalJobs: data.totalJobs,
        completionRate: data.completionRate,
        documentsVerified: data.documentsVerified,
    };
}
// Pre-configured dev users for the auth emulator
// These match the seeded users in scripts/seed-firestore.ts
const DEV_USERS = {
    admin: {
        email: "admin@itemgo.local",
        password: "password123",
        role: "admin",
        displayName: "Platform Admin",
    },
    business: {
        email: "business@itemgo.local",
        password: "password123",
        role: "business",
        displayName: "Acme Logistics Ltd",
    },
    driver: {
        email: "driver@itemgo.local",
        password: "password123",
        role: "driver",
        displayName: "Mike Driver",
    },
    customer: {
        email: "customer@itemgo.local",
        password: "password123",
        role: "customer",
        displayName: "John Customer",
    },
};
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const autoSignInAttempted = useRef(false);
    // Listen to Firebase auth state
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
            setIsLoading(true);
            try {
                if (fbUser) {
                    const mapped = await mapFirebaseUser(fbUser);
                    setUser(mapped);
                }
                else {
                    setUser(null);
                }
                setError(null);
            }
            catch (err) {
                console.error("Auth state mapping error:", err);
                setUser(null);
            }
            finally {
                setIsLoading(false);
            }
        });
        return () => unsubscribe();
    }, []);
    // Dev auto-login: if no user and in emulator mode, sign in as admin automatically
    useEffect(() => {
        if (!isDev || user || isLoading || autoSignInAttempted.current)
            return;
        autoSignInAttempted.current = true;
        const doAutoSignIn = async () => {
            try {
                setIsLoading(true);
                const devAdmin = DEV_USERS.admin;
                try {
                    await signInWithEmailAndPassword(auth, devAdmin.email, devAdmin.password);
                }
                catch (signInErr) {
                    if (signInErr?.code === "auth/user-not-found" || signInErr?.code === "auth/invalid-login-credentials" || signInErr?.message?.includes("INVALID_LOGIN_CREDENTIALS")) {
                        const cred = await createUserWithEmailAndPassword(auth, devAdmin.email, devAdmin.password);
                        await updateProfile(cred.user, { displayName: devAdmin.displayName });
                        await setDoc(doc(db, "users", cred.user.uid), {
                            id: cred.user.uid,
                            email: devAdmin.email,
                            displayName: devAdmin.displayName,
                            role: devAdmin.role,
                            isActive: true,
                            createdAt: serverTimestamp(),
                            updatedAt: serverTimestamp(),
                        });
                    }
                }
            }
            catch (err) {
                console.error("[Auth] Auto sign-in failed:", err);
            }
            finally {
                setIsLoading(false);
            }
        };
        // Small delay to let onAuthStateChanged finish initial check
        const timer = setTimeout(doAutoSignIn, 500);
        return () => clearTimeout(timer);
    }, [isDev, user, isLoading]);
    const signIn = useCallback(async (email, password) => {
        setError(null);
        setIsLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : "Sign in failed";
            setError(message);
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const signUp = useCallback(async (email, password, role, profile) => {
        setError(null);
        setIsLoading(true);
        try {
            const cred = await createUserWithEmailAndPassword(auth, email, password);
            const displayName = profile?.displayName || email.split("@")[0];
            await updateProfile(cred.user, { displayName });
            const userData = {
                id: cred.user.uid,
                email,
                displayName,
                role,
                isActive: true,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };
            if (profile?.phoneNumber)
                userData.phoneNumber = profile.phoneNumber;
            if (profile?.companyName)
                userData.companyName = profile.companyName;
            if (profile?.vehicleType)
                userData.vehicleType = profile.vehicleType;
            if (profile?.licenseNumber)
                userData.licenseNumber = profile.licenseNumber;
            if (profile?.postcode)
                userData.postcode = profile.postcode;
            if (role === "driver") {
                userData.employmentStatus = "independent";
                userData.documentsVerified = false;
                userData.rating = 0;
                userData.totalJobs = 0;
                userData.completionRate = 0;
                userData.isOnline = false;
            }
            if (role === "business") {
                userData.businessRole = "owner";
                userData.permissions = ["manage_fleet", "manage_bookings", "manage_pricing", "post_jobs", "view_analytics", "manage_ads", "billing"];
            }
            if (role === "admin") {
                userData.adminLevel = "support";
            }
            await setDoc(doc(db, "users", cred.user.uid), userData);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : "Sign up failed";
            setError(message);
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const signOut = useCallback(async () => {
        setError(null);
        await firebaseSignOut(auth);
    }, []);
    const getIdToken = useCallback(async () => {
        const fbUser = auth.currentUser;
        if (!fbUser)
            throw new Error("Not authenticated");
        return fbUser.getIdToken();
    }, []);
    const resetPassword = useCallback(async (email) => {
        setError(null);
        await sendPasswordResetEmail(auth, email);
    }, []);
    const switchDevUser = useCallback(async (role) => {
        if (!isDev) {
            console.warn("switchDevUser is only available in development mode");
            return;
        }
        console.log(`[Auth] Switching to dev user: ${role}`);
        setIsLoading(true);
        setError(null);
        try {
            await firebaseSignOut(auth);
            console.log("[Auth] Signed out, waiting for auth state to settle...");
            // Give the emulator a moment to process the sign-out before signing in again
            await new Promise((r) => setTimeout(r, 400));
            const devUser = Object.values(DEV_USERS).find((u) => u.role === role);
            if (!devUser)
                throw new Error(`No dev user configured for role: ${role}`);
            console.log(`[Auth] Signing in as ${devUser.email}...`);
            try {
                await signInWithEmailAndPassword(auth, devUser.email, devUser.password);
            }
            catch (signInErr) {
                // If user doesn't exist, create it first
                if (signInErr?.code === "auth/user-not-found" || signInErr?.code === "auth/invalid-login-credentials" || signInErr?.message?.includes("INVALID_LOGIN_CREDENTIALS")) {
                    console.log("[Auth] User not found, creating dev user...");
                    const cred = await createUserWithEmailAndPassword(auth, devUser.email, devUser.password);
                    await updateProfile(cred.user, { displayName: devUser.displayName });
                    await setDoc(doc(db, "users", cred.user.uid), {
                        id: cred.user.uid,
                        email: devUser.email,
                        displayName: devUser.displayName,
                        role: devUser.role,
                        isActive: true,
                        createdAt: serverTimestamp(),
                        updatedAt: serverTimestamp(),
                    });
                    console.log("[Auth] Dev user created and signed in");
                }
                else {
                    throw signInErr;
                }
            }
            console.log("[Auth] Sign-in successful");
        }
        catch (err) {
            const message = err instanceof Error ? err.message : "Switch failed";
            console.error("[Auth] switchDevUser error:", message);
            setError(message);
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const value = {
        user,
        isLoading,
        isAuthenticated: !!user,
        error,
        signIn,
        signUp,
        signOut,
        getIdToken,
        resetPassword,
        switchDevUser: isDev ? switchDevUser : undefined,
    };
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
//# sourceMappingURL=AuthProvider.js.map